import { groupAndSortEntitySegmentList } from "IDE/utils/groupAndSortEntitySegmentList";
import {
  ActionUrlIcon,
  JsFileIconV2,
} from "pages/Editor/Explorer/ExplorerIcons";
import { createSelector } from "reselect";
import {
  getAllModules,
  getCurrentBaseModuleId,
  getModuleActions,
  getModuleJSCollections,
} from "./modulesSelector";
import { PluginType } from "entities/Plugin";
import type { ParentEntityIDETabs } from "ee/reducers/uiReducers/packageIDEReducer";
import { get, keyBy } from "lodash";
import { EditorEntityTab } from "IDE/Interfaces/EditorTypes";
import { identifyEntityFromPath } from "navigation/FocusEntity";
import { getJSEntityItemUrl } from "ee/pages/UIPackageIDE/layouts/routers/utils/getJSEntityItemUrl";
import { getQueryEntityItemUrl } from "ee/pages/UIPackageIDE/layouts/routers/utils/getQueryEntityItemUrl";
import type { ModuleEntityItem } from "ee/pages/PackageIDECommon/types";
import { getCurrentBasePackageId } from "./packageSelectors";
import { getValues } from "ee/pages/PackageIDECommon/layouts/hooks/useLastVisitedModule";
import { LAST_VISITED_MODULE_KEY } from "ee/pages/UIPackageIDE/constants";
import { moduleEditorURL } from "ee/RouteBuilder";
import history from "utils/history";
import ImageAlt from "assets/images/placeholder-image.svg";
import { getAssetUrl } from "ee/utils/airgapHelpers";
import { getPlugins, selectDatasourceIdToNameMap } from "./entitiesSelector";
import {
  isEmbeddedAIDataSource,
  isEmbeddedRestDatasource,
} from "entities/Datasource";
import type { DefaultRootState } from "react-redux";

export const getUIModuleJSSegmentItems = createSelector(
  getModuleJSCollections,
  getCurrentBaseModuleId,
  (jsActions, baseModuleId) => {
    const items: ModuleEntityItem[] = jsActions.map((js) => ({
      icon: JsFileIconV2(),
      title: js.config.name,
      key: js.config.baseId,
      type: PluginType.JS,
      moduleId: baseModuleId,
      userPermissions: js.config.userPermissions,
    }));

    return items;
  },
);

export const getUIModuleQuerySegmentItems = createSelector(
  getModuleActions,
  getPlugins,
  selectDatasourceIdToNameMap,
  getCurrentBaseModuleId,
  (actions, plugins, datasourceIdToNameMap, baseModuleId) => {
    const pluginGroups = keyBy(plugins, "id");
    const items: ModuleEntityItem[] = actions.map((action) => {
      let group;
      const iconUrl = getAssetUrl(
        pluginGroups[action.config.pluginId]?.iconLocation || ImageAlt,
      );

      if (action.config.pluginType === PluginType.API) {
        group = isEmbeddedRestDatasource(action.config.datasource)
          ? "APIs"
          : datasourceIdToNameMap[action.config.datasource.id] ?? "APIs";
      } else if (action.config.pluginType === PluginType.AI) {
        group = isEmbeddedAIDataSource(action.config.datasource)
          ? "AI Queries"
          : datasourceIdToNameMap[action.config.datasource.id] ?? "AI Queries";
      } else {
        group =
          datasourceIdToNameMap[action.config.datasource?.id] ??
          action.config.datasource?.name;
      }

      return {
        icon: ActionUrlIcon(iconUrl, "16", "16"),
        title: action.config.name,
        key: action.config.baseId,
        type: action.config.pluginType,
        group,
        moduleId: baseModuleId,
        userPermissions: action.config.userPermissions,
      };
    });

    return items;
  },
);

export const selectJSSegmentEditorList = createSelector(
  getUIModuleJSSegmentItems,
  (items) => {
    return groupAndSortEntitySegmentList(items);
  },
);

export const selectQuerySegmentEditorList = createSelector(
  getUIModuleQuerySegmentItems,
  (items) => {
    return groupAndSortEntitySegmentList(items);
  },
);
export const getPackageIDETabs = (state: DefaultRootState) =>
  state.ui.packageide.tabs;

export const getAllJSTabs = createSelector(
  getCurrentBaseModuleId,
  getPackageIDETabs,
  (basePackageId: string, tabs: ParentEntityIDETabs): string[] =>
    get(tabs, [basePackageId, EditorEntityTab.JS], []),
);

export const getAllQueryTabs = createSelector(
  getCurrentBaseModuleId,
  getPackageIDETabs,
  (basePackageId: string, tabs: ParentEntityIDETabs): string[] =>
    get(tabs, [basePackageId, EditorEntityTab.QUERIES], []),
);

export const selectPrivateJSSegmentEditorTabs = createSelector(
  getUIModuleJSSegmentItems,
  getAllJSTabs,
  (items, tabs) => {
    const keyedItems = keyBy(items, "key");

    return tabs
      .map((tab) => {
        return keyedItems[tab];
      })
      .filter(Boolean);
  },
);

export const selectPrivateQuerySegmentEditorTabs = createSelector(
  getUIModuleQuerySegmentItems,
  getAllQueryTabs,
  (items, tabs) => {
    const keyedItems = keyBy(items, "key");

    return tabs
      .map((tab) => {
        return keyedItems[tab];
      })
      .filter(Boolean);
  },
);
export const getLastJSModuleTab = createSelector(
  selectPrivateJSSegmentEditorTabs,
  (tabs) => {
    if (tabs.length) {
      const url = getJSEntityItemUrl(tabs[tabs.length - 1]);
      const urlWithoutQueryParams = url.split("?")[0];

      return identifyEntityFromPath(urlWithoutQueryParams);
    }
  },
);

export const getLastQueryModuleTab = createSelector(
  selectPrivateQuerySegmentEditorTabs,
  (tabs) => {
    if (tabs.length) {
      const url = getQueryEntityItemUrl(tabs[tabs.length - 1]);
      const urlWithoutQueryParams = url.split("?")[0];

      return identifyEntityFromPath(urlWithoutQueryParams);
    }
  },
);

export const getLastVisitedModuleUrl = createSelector(
  getCurrentBasePackageId,
  getAllModules,
  (basePackageId = "", modules) => {
    const values = getValues(LAST_VISITED_MODULE_KEY) || {};
    const module = (Object.values(modules) || [])[0];
    const goToModule = values[basePackageId]
      ? modules[values[basePackageId]]
      : module;

    if (!goToModule) return;

    const entityUrl = moduleEditorURL({
      baseModuleId: goToModule.id,
    });

    return history.replace(entityUrl);
  },
);
